import React, { useEffect, useMemo, useState } from "react";
import Popover from "@mui/material/Popover";
import AppButton from "./AppButton";
import AppText from "./AppText";
import { IconChevronLeft, IconChevronRight } from "../icons";
import {
  getMonthPickerOptions,
  getWeekPickerOptions,
  isSameMonth,
  isSameWeek,
  parseDateValue,
} from "../utils/periodDates";

/*
  Auswahlfenster der Zeitraum-Navigation: im Monatsmodus ein Raster aus zwölf
  Monaten, im Wochenmodus die Wochen eines Monats. Der laufende Zeitraum ist mit
  einem Punkt markiert, der gewählte farbig – damit sieht man beim Öffnen sofort,
  wo man steht.

  Wird normalerweise von AppPeriodNavigator gestellt; einzeln nur, wenn eine
  Seite ihren eigenen Auslöser braucht.
*/

function getReferenceParts(value) {
  const date = parseDateValue(value) || new Date();
  return { year: date.getFullYear(), monthIndex: date.getMonth() };
}

function PickerHeader({ title, displayYear, previousYearLabel, nextYearLabel, onPreviousYear, onNextYear }) {
  return (
    <div className="ds-period-picker__head">
      <div className="ds-period-picker__heading">
        <AppText variant="label-1" className="ds-period-picker__title">{title}</AppText>
        <AppText variant="heading-4" className="ds-period-picker__year">{displayYear}</AppText>
      </div>

      <div className="ds-period-picker__year-nav">
        <AppButton variant="muted" size="sm" iconOnly aria-label={previousYearLabel} onClick={onPreviousYear}>
          <IconChevronLeft sx={{ fontSize: 18 }} />
        </AppButton>
        <AppButton variant="muted" size="sm" iconOnly aria-label={nextYearLabel} onClick={onNextYear}>
          <IconChevronRight sx={{ fontSize: 18 }} />
        </AppButton>
      </div>
    </div>
  );
}

function MonthPicker({ displayYear, referenceDate, currentDateLabel, onSelectDate }) {
  const currentDate = new Date();
  const monthOptions = useMemo(() => getMonthPickerOptions(displayYear), [displayYear]);

  return (
    <div className="ds-period-picker__month-grid" role="listbox" aria-label={undefined}>
      {monthOptions.map((option) => {
        const isSelected = isSameMonth(option.date, referenceDate);
        const isCurrent = isSameMonth(option.date, currentDate);

        return (
          <button
            key={option.key}
            type="button"
            role="option"
            aria-selected={isSelected}
            className={`ds-period-picker__month${isSelected ? " is-selected" : ""}${isCurrent ? " is-current" : ""}`}
            onClick={() => onSelectDate(option.date)}
          >
            <span className="ds-period-picker__month-label">{option.label}</span>
            {isCurrent ? <span className="ds-period-picker__dot" aria-label={currentDateLabel} /> : null}
          </button>
        );
      })}
    </div>
  );
}

function WeekPicker({
  displayYear,
  displayMonth,
  referenceDate,
  monthStripLabel,
  currentDateLabel,
  onDisplayMonthChange,
  onSelectDate,
}) {
  const currentDate = new Date();
  const monthOptions = useMemo(() => getMonthPickerOptions(displayYear), [displayYear]);
  const weekOptions = useMemo(() => getWeekPickerOptions(displayYear, displayMonth), [displayMonth, displayYear]);

  return (
    <div className="ds-period-picker__week">
      <div className="ds-period-picker__month-strip" role="group" aria-label={monthStripLabel}>
        {monthOptions.map((option) => {
          const isSelectedMonth = option.monthIndex === displayMonth;
          const isCurrentMonth = isSameMonth(option.date, currentDate);

          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={isSelectedMonth}
              className={`ds-period-picker__month-chip${isSelectedMonth ? " is-selected" : ""}${
                isCurrentMonth ? " is-current" : ""
              }`}
              onClick={() => onDisplayMonthChange(option.monthIndex)}
            >
              <span>{option.shortLabel}</span>
              {isCurrentMonth ? <span className="ds-period-picker__chip-dot" aria-label={currentDateLabel} /> : null}
            </button>
          );
        })}
      </div>

      <div className="ds-period-picker__week-list" role="listbox">
        {weekOptions.map((option) => {
          const isSelected = isSameWeek(option.date, referenceDate);
          const isCurrent = isSameWeek(option.date, currentDate);

          return (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={`ds-period-picker__week-row${isSelected ? " is-selected" : ""}${
                isCurrent ? " is-current" : ""
              }`}
              onClick={() => onSelectDate(option.date)}
            >
              <span className="ds-period-picker__week-main">{option.label}</span>
              <span className="ds-period-picker__week-range">{option.rangeLabel}</span>
              {isCurrent ? <span className="ds-period-picker__dot" aria-label={currentDateLabel} /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AppPeriodPicker({
  open = false,
  anchorEl = null,
  viewMode = "week",
  referenceDate,
  weekTitle = "Woche wählen",
  monthTitle = "Monat wählen",
  monthStripLabel = "Monat der Woche",
  currentPeriodLabel = "Aktueller Zeitraum",
  previousYearLabel = "Vorheriges Jahr",
  nextYearLabel = "Nächstes Jahr",
  onClose,
  onSelectDate,
}) {
  const isWeek = viewMode !== "month";
  const referenceParts = getReferenceParts(referenceDate);
  const [displayYear, setDisplayYear] = useState(referenceParts.year);
  const [displayMonth, setDisplayMonth] = useState(referenceParts.monthIndex);

  useEffect(() => {
    if (!open) return;
    const nextParts = getReferenceParts(referenceDate);
    setDisplayYear(nextParts.year);
    setDisplayMonth(nextParts.monthIndex);
  }, [open, referenceDate]);

  function handleSelectDate(date) {
    onSelectDate?.(date);
    onClose?.();
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ className: "ds-period-picker__paper" }}
    >
      <div className={`ds-period-picker${isWeek ? " ds-period-picker--week" : " ds-period-picker--month"}`}>
        <PickerHeader
          title={isWeek ? weekTitle : monthTitle}
          displayYear={displayYear}
          previousYearLabel={previousYearLabel}
          nextYearLabel={nextYearLabel}
          onPreviousYear={() => setDisplayYear((current) => current - 1)}
          onNextYear={() => setDisplayYear((current) => current + 1)}
        />

        {isWeek ? (
          <WeekPicker
            displayYear={displayYear}
            displayMonth={displayMonth}
            referenceDate={referenceDate}
            monthStripLabel={monthStripLabel}
            currentDateLabel={currentPeriodLabel}
            onDisplayMonthChange={setDisplayMonth}
            onSelectDate={handleSelectDate}
          />
        ) : (
          <MonthPicker
            displayYear={displayYear}
            referenceDate={referenceDate}
            currentDateLabel={currentPeriodLabel}
            onSelectDate={handleSelectDate}
          />
        )}
      </div>
    </Popover>
  );
}
